import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import paths from 'routes/paths';
import { isMymActive, getAtHomePatients } from 'helpers/isSharedLivingActive';
import { normalizeAtHomePatients } from './MyPatientsList';

import FacilityMedSheetForm from 'components/reports/FacilityMedSheetForm';
import Treatment from 'components/reports/Treatment';
import CurrentMed from 'components/reports/CurrentMed';
import LoaForm from 'components/reports/LoaForm';
import MedTracking from 'components/reports/MedTracking';
import PatientProfileReport from 'components/reports/PatientProfileReport';
import EFSReport from 'components/reports/EFSReport';

// SL-002: caregivers get exactly these 7 reports.
// Treatment / Current Meds / LOA are tier-gated by is_mym_active (SL-042b).
const ALL_REPORTS = [
  { value: 'medsheet', label: 'Medsheet', tierGated: false },
  { value: 'treatment', label: 'Treatment', tierGated: true },
  { value: 'currentmeds', label: 'Current Meds', tierGated: true },
  { value: 'loaform', label: 'LOA', tierGated: true },
  { value: 'medtrackui', label: 'Med Tracking', tierGated: false },
  { value: 'patientprofile', label: 'Patient Profile', tierGated: false },
  { value: 'efsreport', label: 'EFS', tierGated: false }
];

/**
 * UI-038/039/040/041 (Phase 8): caregiver-facing report page. Patient picker is
 * scoped to the caregiver's assigned patients only (SL-042a — never
 * facility/group-wide). Reuses the existing report form components; the report
 * list is limited to the 7 SL-002 reports and respects is_mym_active.
 */
const CaregiverReports = () => {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [reportType, setReportType] = useState('medsheet');
  const [loading, setLoading] = useState(true);

  const mymActive = isMymActive();

  const reportOptions = useMemo(
    () => ALL_REPORTS.filter(r => !r.tierGated || mymActive),
    [mymActive]
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const tokenStr = localStorage.getItem('accessToken');
        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/athome/my-patients`,
          { headers: { Authorization: `Bearer ${tokenStr}` } }
        );
        setPatients(normalizeAtHomePatients(data));
      } catch (error) {
        setPatients([]);
        toast.error('Unable to load your patients');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // When a patient is chosen, prime the localStorage keys the shared report
  // form components read. Scope is always a single assigned patient.
  const handlePatientChange = opt => {
    setSelectedPatient(opt);
    if (opt) {
      // Defence-in-depth: only allow patients in the assignment list.
      if (!getAtHomePatients().map(String).includes(String(opt.value))) {
        toast.error('You are not authorized to report on this patient.');
        setSelectedPatient(null);
        return;
      }
      localStorage.setItem('patientId', opt.value);
      localStorage.setItem('reportFacilityId', opt.facility_id || '');
      localStorage.setItem('facilityId', opt.facility_id || '');
      localStorage.setItem('patientName', opt.label);
    } else {
      localStorage.removeItem('patientId');
    }
  };

  const patientSelectOptions = patients.map(p => ({
    value: p.id,
    label: p.name,
    facility_id: p.facilityId || p.facility_id
  }));

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Reports', href: paths.caregiverReports, isActive: true }
  ];

  const reportData = selectedPatient
    ? { patientid: selectedPatient.value, id: selectedPatient.value }
    : {};

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <Card className="mb-3">
        <Card.Header as="h6" className="bg-body-tertiary">Reports</Card.Header>
        <Card.Body className="p-3">
          {!mymActive && (
            <Alert variant="secondary" className="py-2 fs-11">
              <FontAwesomeIcon icon="info-circle" className="me-2" />
              Treatment, Current Meds and LOA reports are unavailable for this group's plan.
            </Alert>
          )}
          <Row className="g-3">
            <Col xs="12" md="6">
              <Form.Group>
                <Form.Label className="required">Patient</Form.Label>
                <ThemedSelect
                  options={patientSelectOptions}
                  value={selectedPatient}
                  isLoading={loading}
                  onChange={handlePatientChange}
                  placeholder="Select one of your patients"
                />
              </Form.Group>
            </Col>
            <Col xs="12" md="6">
              <Form.Group>
                <Form.Label className="required">Report Type</Form.Label>
                <Form.Select
                  size="sm"
                  value={reportType}
                  onChange={e => setReportType(e.target.value)}
                  disabled={!selectedPatient}
                >
                  {reportOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {selectedPatient ? (
            <Row className="mt-3">
              <Col xs="12">
                {reportType === 'medsheet' && (
                  <FacilityMedSheetForm formname="Medsheet" type="patient" data={reportData} />
                )}
                {reportType === 'treatment' && mymActive && (
                  <Treatment type="patient" data={reportData} />
                )}
                {reportType === 'currentmeds' && mymActive && (
                  <CurrentMed type="patient" data={reportData} />
                )}
                {reportType === 'loaform' && mymActive && (
                  <LoaForm data={reportData} />
                )}
                {reportType === 'medtrackui' && <MedTracking />}
                {reportType === 'patientprofile' && <PatientProfileReport />}
                {reportType === 'efsreport' && <EFSReport />}
              </Col>
            </Row>
          ) : (
            <div className="text-center text-600 py-4">
              Select a patient to generate a report.
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default CaregiverReports;
